import { FC } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled, Theme } from '@mui/material/styles';
import { Row } from '../Layout';

export type TableOfContentsMenuEntry = {
  id: string;
  number: string;
  label: string;
  isSubEntry: boolean;
};

const EntryNumberBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSubEntry',
})<{ isSubEntry?: boolean }>(({ isSubEntry }) => ({
  width: isSubEntry ? 21 : 18,
  minWidth: isSubEntry ? 21 : 18,
  maxWidth: isSubEntry ? 21 : 18,
  flexShrink: 0,
}));

const getEntryTextStyles = (theme: Theme) => ({
  fontSize: theme.typography.h6.fontSize,
  lineHeight: theme.typography.h6.lineHeight,
  fontWeight: theme.typography.fontWeightMedium,
});

const EntryNumberText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  ...getEntryTextStyles(theme),
  color: selected ? theme.palette.grey[600] : theme.palette.grey[500],
}));

const EntryLabelText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  ...getEntryTextStyles(theme),
  color: selected ? theme.palette.grey[900] : theme.palette.grey[500],
}));

const EntryRow = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'isSubEntry' && prop !== 'selected',
})<{ isSubEntry?: boolean; selected?: boolean }>(
  ({ theme, isSubEntry, selected }) => ({
    width: '100%',
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,
    paddingTop: theme.spacing(theme.spacingSizes.xs),
    paddingBottom: theme.spacing(theme.spacingSizes.xs),
    paddingRight: theme.spacing(theme.spacingSizes.sm),
    paddingLeft: isSubEntry
      ? theme.spacing(10)
      : theme.spacing(theme.spacingSizes.sm),
    backgroundColor: selected ? theme.palette.grey[100] : 'transparent',

    '&:hover': {
      backgroundColor: selected
        ? theme.palette.grey[100]
        : theme.palette.action.hover,
    },
  }),
);

type Props = {
  entry: TableOfContentsMenuEntry;
  selected: boolean;
  onSelect: (id: string) => void;
};

const TableOfContentsEntry: FC<Props> = ({ entry, selected, onSelect }) => (
  <EntryRow
    isSubEntry={entry.isSubEntry}
    selected={selected}
    spacing="xs"
    centered
    role="button"
    tabIndex={0}
    onClick={() => {
      onSelect(entry.id);
    }}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(entry.id);
      }
    }}
  >
    <EntryNumberBox isSubEntry={entry.isSubEntry}>
      <EntryNumberText selected={selected}>{entry.number}</EntryNumberText>
    </EntryNumberBox>
    <EntryLabelText selected={selected}>{entry.label}</EntryLabelText>
  </EntryRow>
);

export default TableOfContentsEntry;
